class CollectivePersonalisation extends HTMLElement {
  constructor() {
    super();
    this.enabled = this.dataset.enabled === 'true';
    this.active = this.dataset.active === 'true';
    this.checkbox = null;
    this.fieldsWrapper = null;
    this.productForm = null;
    this.submitInterceptor = null;
    this.variantChangeUnsubscriber = null;
    this.resizePreviewHandler = null;
  }

  connectedCallback() {
    if (!this.enabled) return;

    this.checkbox = this.querySelector('.collective-personalisation__checkbox');
    this.fieldsWrapper = this.querySelector('[data-collective-fields]');

    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.onCheckboxChange.bind(this));
      this.onCheckboxChange();
    }

    this.querySelectorAll('[data-collective-field] input').forEach((input) => {
      input.addEventListener('input', () => {
        this.validateInput(input);
        if (this.canShowPreview()) this.updatePreviewText();
      });
    });

    if (this.dataset.previewMediaId) {
      this.initPreview();
      this.bindPreviewResize();
    }

    if (this.active) {
      this.loadAddonPrice();

      customElements.whenDefined('product-form').then(() => {
        const formElement = document.getElementById(this.dataset.formId);
        if (!formElement) return;

        this.productForm = formElement.closest('product-form');
        if (!this.productForm) return;

        const form = this.productForm.querySelector('form');
        if (!form) return;

        this.submitInterceptor = (event) => {
          if (!this.shouldHandleSubmit()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.onSubmitHandler(event);
        };

        form.addEventListener('submit', this.submitInterceptor, true);
      });
    }
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = null;
    }

    this.removePreviewOverlays();
    this.unbindPreviewResize();

    if (!this.productForm || !this.submitInterceptor) return;
    const form = this.productForm.querySelector('form');
    if (form) form.removeEventListener('submit', this.submitInterceptor, true);
  }

  canShowPreview() {
    return this.enabled && this.checkbox?.checked && Boolean(this.dataset.previewMediaId);
  }

  isDynamicTextSizing() {
    return this.dataset.dynamicTextSizing === 'true';
  }

  bindPreviewResize() {
    if (!this.isDynamicTextSizing() || this.resizePreviewHandler) return;

    this.resizePreviewHandler = () => {
      if (this.canShowPreview()) this.updatePreviewText();
    };

    window.addEventListener('resize', this.resizePreviewHandler);
  }

  unbindPreviewResize() {
    if (!this.resizePreviewHandler) return;
    window.removeEventListener('resize', this.resizePreviewHandler);
    this.resizePreviewHandler = null;
  }

  getMaxFontSize(overlay, cssVar) {
    const value = getComputedStyle(overlay).getPropertyValue(cssVar).trim();
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 24;
  }

  getDynamicTextMaxWidth(overlay) {
    const value = getComputedStyle(overlay).getPropertyValue('--collective-preview-dynamic-text-width').trim();
    const parsed = parseFloat(value);
    const widthPercent = Number.isFinite(parsed) ? parsed / 100 : 0.9;
    return overlay.clientWidth * widthPercent;
  }

  fitPreviewTextToWidth(element, overlay, cssVar) {
    if (!this.isDynamicTextSizing()) {
      element.style.fontSize = '';
      return;
    }

    const text = element.textContent.trim();
    if (!text) {
      element.style.fontSize = '';
      return;
    }

    const maxSize = this.getMaxFontSize(overlay, cssVar);
    const minSize = 8;
    const maxWidth = this.getDynamicTextMaxWidth(overlay);

    element.style.fontSize = `${maxSize}px`;
    if (element.scrollWidth <= maxWidth) return;

    let low = minSize;
    let high = maxSize;
    let best = minSize;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      element.style.fontSize = `${mid}px`;
      if (element.scrollWidth <= maxWidth) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    element.style.fontSize = `${best}px`;
  }

  initPreview() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
        if (this.canShowPreview()) this.activatePreview();
      });
    }

    if (this.canShowPreview()) this.activatePreview();
  }

  getMediaGalleries() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return [];

    return [`MediaGallery-${sectionId}`, `MediaGallery-${sectionId}-duplicate`]
      .map((id) => document.getElementById(id))
      .filter(Boolean);
  }

  getGalleryViewer(gallery) {
    return gallery.querySelector('[id^="GalleryViewer"]');
  }

  setGalleryToPreviewMedia(gallery, mediaId) {
    const viewer = this.getGalleryViewer(gallery);
    if (!viewer) return false;

    const slide = viewer.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return false;

    try {
      if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(mediaId, false);
        return true;
      }
    } catch (error) {
      console.warn('collective-personalisation: setActiveMedia failed', error);
    }

    viewer.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('is-active'));
    slide.classList.add('is-active');
    return true;
  }

  getPreviewMountContainers() {
    const mediaId = this.dataset.previewMediaId;
    const containers = [];

    this.getMediaGalleries().forEach((gallery) => {
      const viewer = this.getGalleryViewer(gallery);
      if (!viewer || !mediaId) return;

      const slide = viewer.querySelector(`[data-media-id="${mediaId}"]`);
      const container = slide?.querySelector('.product-media-container');
      if (container) containers.push(container);
    });

    if (containers.length > 0) return containers;

    this.getMediaGalleries().forEach((gallery) => {
      const container = gallery.querySelector('.product__media-item.is-active .product-media-container');
      if (container) containers.push(container);
    });

    return containers;
  }

  getPreviewStyle() {
    return this.getAttribute('style') || '';
  }

  removePreviewOverlays() {
    document.querySelectorAll(`[data-collective-preview-owner="${this.id}"]`).forEach((overlay) => overlay.remove());
  }

  mountPreviewOverlays() {
    const template = this.querySelector('template');
    if (!template?.content?.firstElementChild) return;

    this.removePreviewOverlays();
    const previewStyle = this.getPreviewStyle();
    const containers = this.getPreviewMountContainers();

    containers.forEach((container) => {
      const overlay = template.content.firstElementChild.cloneNode(true);
      overlay.dataset.collectivePreviewOwner = this.id;
      overlay.removeAttribute('hidden');
      if (previewStyle) overlay.setAttribute('style', previewStyle);
      if (this.isDynamicTextSizing()) {
        overlay.classList.add('collective-preview--dynamic-sizing');
      }
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      container.appendChild(overlay);
    });
  }

  activatePreview() {
    // TEMP: remove after testing personalisation preview
    if (!this.canShowPreview()) {
      console.log('[collective-personalisation] preview skipped', {
        enabled: this.enabled,
        checkboxChecked: this.checkbox?.checked,
        previewMediaId: this.dataset.previewMediaId || null,
        sectionId: this.dataset.sectionId || null,
      });
      return;
    }

    const mediaId = this.dataset.previewMediaId;

    console.log('[collective-personalisation] attempting to show preview', {
      mediaId,
      sectionId: this.dataset.sectionId,
      galleries: this.getMediaGalleries().length,
    });

    const activate = () => {
      const galleryResults = this.getMediaGalleries().map((gallery) => ({
        id: gallery.id,
        switched: this.setGalleryToPreviewMedia(gallery, mediaId),
      }));

      console.log('[collective-personalisation] gallery switch', galleryResults);

      window.setTimeout(() => {
        this.mountPreviewOverlays();
        this.updatePreviewText();
        const overlays = document.querySelectorAll(`[data-collective-preview-owner="${this.id}"]`);
        console.log('[collective-personalisation] preview mounted', {
          overlayCount: overlays.length,
          containers: this.getPreviewMountContainers().length,
          bottomElements: document.querySelectorAll('[data-collective-preview-bottom]').length,
        });
      }, 350);
    };

    if (customElements.get('media-gallery')) {
      activate();
    } else {
      customElements.whenDefined('media-gallery').then(activate);
    }
  }

  deactivatePreview() {
    this.removePreviewOverlays();
  }

  updatePreviewText() {
    if (!this.canShowPreview()) return;

    const topInput = this.querySelector('.collective-personalisation__top-input');
    const bottomInput = this.querySelector('.collective-personalisation__bottom-input');
    const topText = topInput?.value.trim() || '';
    const bottomText = bottomInput?.value.trim() || '';

    document.querySelectorAll(`[data-collective-preview-owner="${this.id}"]`).forEach((overlay) => {
      const topEl = overlay.querySelector('[data-collective-preview-top]');
      const bottomEl = overlay.querySelector('[data-collective-preview-bottom]');

      if (topEl) {
        topEl.textContent = topText;
        this.fitPreviewTextToWidth(topEl, overlay, '--collective-preview-top-size');
      }
      if (bottomEl) {
        bottomEl.textContent = bottomText;
        this.fitPreviewTextToWidth(bottomEl, overlay, '--collective-preview-bottom-size');
      }
    });
  }

  onCheckboxChange() {
    if (!this.fieldsWrapper || !this.checkbox) return;

    if (this.checkbox.checked) {
      this.fieldsWrapper.classList.remove('hidden');
      this.activatePreview();
    } else {
      this.fieldsWrapper.classList.add('hidden');
      this.clearFieldErrors();
      this.deactivatePreview();
    }
  }

  clearFieldErrors() {
    this.querySelectorAll('[data-collective-field]').forEach((wrapper) => {
      this.setInputError(wrapper.querySelector('input'), false);
    });
    if (this.productForm) this.productForm.handleErrorMessage(false);
  }

  shouldHandleSubmit() {
    return this.active && this.checkbox && this.checkbox.checked;
  }

  validateInput(input) {
    if (!input) return true;

    let hasError = false;
    let errorMessage = '';
    const value = input.value;
    const wrapper = input.closest('[data-collective-field]');

    if (input.hasAttribute('data-collective-required') && value.trim().length < 1) {
      hasError = true;
      errorMessage = 'This is a required field.';
    }

    if (!hasError && input.hasAttribute('minlength') && value.length < parseInt(input.getAttribute('minlength'), 10)) {
      hasError = true;
      errorMessage = `Length should be at least ${input.getAttribute('minlength')} characters`;
    }

    if (!hasError && input.hasAttribute('maxlength') && value.length > parseInt(input.getAttribute('maxlength'), 10)) {
      hasError = true;
      errorMessage = `Length should not exceed ${input.getAttribute('maxlength')} characters`;
    }

    if (!hasError && value.length > 0 && input.hasAttribute('data-validation-filter')) {
      const filter = new RegExp(input.getAttribute('data-validation-filter'));
      if (!filter.test(value)) {
        hasError = true;
        errorMessage = input.getAttribute('data-validation-error-message') || 'Invalid format. Please change and retry';
      }
    }

    this.setInputError(input, hasError, errorMessage);
    return !hasError;
  }

  setInputError(input, hasError, errorMessage = '') {
    const wrapper = input.closest('[data-collective-field]');
    if (!wrapper) return;

    const errorField = wrapper.querySelector('.product-form__error-message-wrapper');
    const errorText = wrapper.querySelector('.product-form__error-message');

    if (hasError) {
      wrapper.classList.add('error');
      if (errorField) errorField.hidden = false;
      if (errorText) errorText.textContent = errorMessage;
    } else {
      wrapper.classList.remove('error');
      if (errorField) errorField.hidden = true;
      if (errorText) errorText.textContent = '';
    }
  }

  validateFields() {
    let passed = true;
    this.querySelectorAll('[data-collective-field] input').forEach((input) => {
      if (!this.validateInput(input)) passed = false;
    });

    if (!passed && this.productForm) {
      this.productForm.handleErrorMessage('1 or more fields are incorrect. Please correct them before continuing.');
    }

    return passed;
  }

  generateGroupId() {
    return `cg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  getStorefrontRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  async loadAddonPrice() {
    const override = (this.dataset.priceLabelOverride || '').trim();
    if (override) return;

    const handle = (this.dataset.addonHandle || '').trim();
    const variantId = (this.dataset.personalisationVariantId || '').trim();
    const display = this.querySelector('[data-collective-price-display]');
    if (!display || !handle || !variantId) return;

    try {
      const response = await fetch(`${this.getStorefrontRoot()}products/${handle}.js`);
      if (!response.ok) return;

      const product = await response.json();
      const variant = product.variants?.find((entry) => String(entry.id) === String(variantId));
      if (!variant?.price) return;

      display.textContent = `(+ ${this.formatMoney(variant.price)})`;
      display.hidden = false;
      this.dataset.personalisationPrice = String(variant.price);
    } catch (error) {
      console.error(error);
    }
  }

  getCartItemsFromResponse(response) {
    if (Array.isArray(response.items)) return response.items;
    if (response.items && typeof response.items === 'object') return Object.values(response.items);
    return [];
  }

  findMainCartItem(response, mainVariantId) {
    const items = this.getCartItemsFromResponse(response);
    return (
      items.find((item) => String(item.variant_id) === String(mainVariantId)) ||
      items.find((item) => item.properties?._collective_link === 'main') ||
      items[0]
    );
  }

  renderCartNotificationContents(response, mainVariantId) {
    const cart = this.productForm.cart;
    if (!cart) return;

    if (cart.tagName !== 'CART-NOTIFICATION') {
      cart.renderContents(response);
      return;
    }

    const sections = response && response.sections ? response.sections : null;
    if (!sections) {
      return;
    }

    const requiredSections = cart.getSectionsToRender().map((section) => section.id);
    const hasAllSections = requiredSections.every((sectionId) => Boolean(sections[sectionId]));
    if (!hasAllSections) {
      return;
    }

    const mainItem = this.findMainCartItem(response, mainVariantId);
    if (mainItem?.key) {
      response.key = mainItem.key;
    }

    const sectionHtml = sections['cart-notification-product'];
    const productContainer = document.getElementById('cart-notification-product');

    if (sectionHtml && productContainer && mainItem?.key) {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const sectionRoot =
        doc.querySelector('#shopify-section-cart-notification-product') || doc.querySelector('.shopify-section');
      const escapedKey = String(mainItem.key).replace(/:/g, '\\:');
      const productNode =
        sectionRoot?.querySelector(`#cart-notification-product-${escapedKey}`) ||
        sectionRoot?.querySelector(`[id="cart-notification-product-${mainItem.key}"]`);

      if (productNode) {
        productContainer.innerHTML = productNode.outerHTML;
      } else if (sectionRoot) {
        productContainer.innerHTML = sectionRoot.innerHTML;
      }

      cart.getSectionsToRender().forEach((section) => {
        if (section.id === 'cart-notification-product') return;
        const element = document.getElementById(section.id);
        const html = sections[section.id];
        if (!element || !html) return;

        const inner = cart.getSectionInnerHTML(html, section.selector);
        if (inner != null) element.innerHTML = inner;
      });

      if (cart.header) cart.header.reveal();
      cart.open();
      return;
    }

    cart.renderContents(response);
  }

  getFormProperties(form) {
    const properties = {};
    const formData = new FormData(form);

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || value === '') return;
      properties[match[1]] = value;
    });

    return properties;
  }

  getVisibleTextProperties() {
    const properties = {};
    const topInput = this.querySelector('.collective-personalisation__top-input');
    const bottomInput = this.querySelector('.collective-personalisation__bottom-input');

    if (topInput && topInput.value.trim() !== '') {
      properties[topInput.dataset.collectivePropertyName] = topInput.value.trim();
    }
    if (bottomInput && bottomInput.value.trim() !== '') {
      properties[bottomInput.dataset.collectivePropertyName] = bottomInput.value.trim();
    }

    return properties;
  }

  onSubmitHandler() {
    if (!this.productForm) return;
    if (this.productForm.submitButton.getAttribute('aria-disabled') === 'true') return;
    if (!this.validateFields()) return;

    const form = this.productForm.querySelector('form');
    const variantId = form.querySelector('[name="id"]').value;
    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
    const personalisationVariantId = this.dataset.personalisationVariantId;

    if (!personalisationVariantId) {
      this.productForm.handleErrorMessage('Personalisation is not available right now. Please try again.');
      return;
    }
    const groupId = this.generateGroupId();

    const mainProperties = {
      ...this.getFormProperties(form),
      ...this.getVisibleTextProperties(),
      _collective_link: 'main',
      _collective_group: groupId,
      _collective_personalisation_variant_id: String(personalisationVariantId),
    };

    const childProperties = {
      _collective_link: 'personalisation',
      _collective_group: groupId,
      _collective_parent_variant_id: String(variantId),
    };

    const payload = {
      items: [
        {
          id: variantId,
          quantity: quantity,
          properties: mainProperties,
        },
        {
          id: personalisationVariantId,
          quantity: quantity,
          properties: childProperties,
        },
      ],
    };

    if (this.productForm.cart) {
      payload.sections = this.productForm.cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      this.productForm.cart.setActiveElement(document.activeElement);
    }

    this.productForm.submitButton.setAttribute('aria-disabled', true);
    this.productForm.submitButton.classList.add('loading');
    this.productForm.querySelector('.loading-overlay__spinner').classList.remove('hidden');
    this.productForm.handleErrorMessage();

    fetch(`${routes.cart_add_url}`, {
      ...fetchConfig(),
      headers: {
        ...fetchConfig().headers,
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'collective-personalisation',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.productForm.handleErrorMessage(response.description);
          this.productForm.error = true;
          return;
        }

        if (!this.productForm.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'collective-personalisation', productVariantId: variantId });
        this.productForm.error = false;
        this.renderCartNotificationContents(response, variantId);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.productForm.submitButton.classList.remove('loading');
        if (this.productForm.cart && this.productForm.cart.classList.contains('is-empty')) {
          this.productForm.cart.classList.remove('is-empty');
        }
        if (!this.productForm.error) this.productForm.submitButton.removeAttribute('aria-disabled');
        this.productForm.querySelector('.loading-overlay__spinner').classList.add('hidden');
      });
  }
}

customElements.define('collective-personalisation', CollectivePersonalisation);
